/**
 * Marketing-strategy — shared presentational helpers (self-contained; the
 * shared `@/ui` kit is never edited). FR-first, token-themed, dark-mode-safe.
 */

import { type ReactNode } from 'react';
import { useT, tr } from '@/i18n';

/** Default channel chips offered in the brief form (free mix if none picked). */
export const STRATEGY_CHANNELS: { value: string; label: string }[] = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'x', label: tr('routes.strategyMktg.strategyUi.xTwitter') },
  { value: 'email', label: tr('routes.strategyMktg.strategyUi.newsletterEmail') },
  { value: 'sms', label: 'SMS' },
  { value: 'blog', label: tr('routes.strategyMktg.strategyUi.blogSeo') },
  { value: 'ads', label: tr('routes.strategyMktg.strategyUi.paidAds') },
];

export const CHANNEL_LABELS: Record<string, string> = Object.fromEntries(
  STRATEGY_CHANNELS.map((c) => [c.value, c.label]),
);

/**
 * Honest "bientôt disponible" panel for a 404/501/503 — the backend is in
 * flight. Never implies the data is empty; says the capability is not live yet.
 */
export function UnavailableNotice({
  title = tr('routes.strategyMktg.strategyUi.comingSoon'),
  message,
}: {
  title?: string;
  message?: ReactNode;
}) {
  const { t } = useT();
  return (
    <div className="rounded-token-lg border border-dashed border-border bg-surface-2/40 px-5 py-8 text-center">
      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-surface-2 text-xl">
        🛠️
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">
        {message ??
          t('routes.strategyMktg.strategyUi.thisCapabilityIsNotEnabledOn')}
      </p>
    </div>
  );
}

/**
 * The Loi-25 / CASL autonomy notice shown on the launch controls. Autonomy is
 * off by default; this explains that nothing is sent without the owner's
 * deliberate choice and that the compliance gate still bites at every level.
 */
export function AutonomyNotice() {
  const { t } = useT();
  return (
    <div className="rounded-token-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
      <div className="mb-1 flex items-center gap-2 font-semibold text-foreground">
        <span aria-hidden>🛡️</span>{t('routes.strategyMktg.strategyUi.autonomyComplianceLaw25Casl')}</div>
      <p className="text-muted">{t('routes.strategyMktg.strategyUi.eachChannelIs')}{' '}<strong className="text-foreground">{t('routes.strategyMktg.strategyUi.offByDefault')}</strong>{t('routes.strategyMktg.strategyUi.youExplicitlyChooseChannelByChannel')}</p>
    </div>
  );
}

/** Shared control classes (match the app's form look without touching @/ui). */
export const inputClass =
  'w-full rounded-token border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60';

export const primaryBtn =
  'rounded-token bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-elevation-1 transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export const secondaryBtn =
  'rounded-token border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export const dangerBtn =
  'rounded-token border border-danger/40 px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/** Chip-style multi-select toggle button. */
export function ChipToggle({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition disabled:opacity-50 ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}
